import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import {
  LayoutDashboard,
  Scissors,
  Image,
  Star,
  CalendarDays,
  MessageSquare,
  LogOut,
  Plus,
  Trash2,
  Pencil,
  X,
  Save,
  Upload,
  Loader2,
  Bell,
  CheckCircle,
  Sparkles,
  Megaphone,
  ExternalLink,
  Copy,
  Eye,
  EyeOff,
  Send,
  Phone,
  Users,
  Mail,
  CalendarPlus,
  AlertTriangle,
  RotateCcw,
} from "lucide-react";
import { API, SITE_URL, authHeaders, resolveUrl, ImageUploadField } from "./shared";

const PromotionsTab = () => {
  const [contacts, setContacts] = useState([]);
  const [history, setHistory] = useState([]);
  const [sending, setSending] = useState(false);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: "", phone: "", email: "" });
  const [form, setForm] = useState({
    title: "",
    message: "",
    channels: ["sms", "email"],
    recipient_filter: "all",
    custom_numbers: "",
  });

  const templates = [
    { title: "New Client Special", message: "Hey {name}! First time at Afro Magic? Get 15% OFF your first braiding session! Book online at afromagicbraiding.shop and save even more with our 10% online discount. Call (313) 484-7307" },
    { title: "Holiday Special", message: "Happy Holidays from Afro Magic! Book any braiding style this week and get $20 OFF. Limited spots available! Book now: afromagicbraiding.shop | (313) 484-7307" },
    { title: "Referral Bonus", message: "Love your braids? Refer a friend to Afro Magic and you BOTH get $15 OFF your next appointment! Just mention this text when booking. (313) 484-7307" },
    { title: "Flash Sale", message: "FLASH SALE! Knotless braids starting at $180 (reg $200) this weekend only at Afro Magic! Book now before spots fill up: afromagicbraiding.shop | (313) 484-7307" },
  ];

  useEffect(() => {
    axios.get(`${API}/admin/contacts`, authHeaders()).then(r => setContacts(r.data)).catch((e) => console.error("Failed to load contacts:", e));
    axios.get(`${API}/admin/promo/history`, authHeaders()).then(r => setHistory(r.data)).catch((e) => console.error("Failed to load promo history:", e));
  }, []);

  const handleSend = async () => {
    if (!form.title || !form.message) {
      toast.error("Please fill in the offer title and message");
      return;
    }
    if (form.channels.length === 0) {
      toast.error("Select at least one channel (SMS, WhatsApp, or Email)");
      return;
    }
    setSending(true);
    try {
      const payload = {
        ...form,
        custom_numbers: form.recipient_filter === "custom" ? form.custom_numbers.split(",").map(n => n.trim()).filter(Boolean) : [],
      };
      const res = await axios.post(`${API}/admin/promo/send`, payload, authHeaders());
      const r = res.data;
      toast.success(`Sent! SMS: ${r.sms_sent}, WhatsApp: ${r.whatsapp_sent}, Email: ${r.email_sent}`);
      setForm({ title: "", message: "", channels: ["sms", "email"], recipient_filter: "all", custom_numbers: "" });
      const histRes = await axios.get(`${API}/admin/promo/history`, authHeaders());
      setHistory(histRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSending(false);
    }
  };

  const handleAddContact = async () => {
    if (!newContact.phone) { toast.error("Phone number required"); return; }
    try {
      await axios.post(`${API}/admin/contacts`, newContact, authHeaders());
      toast.success("Contact added!");
      setNewContact({ name: "", phone: "", email: "" });
      setShowAddContact(false);
      const res = await axios.get(`${API}/admin/contacts`, authHeaders());
      setContacts(res.data);
    } catch { toast.error("Failed to add"); }
  };

  const toggleChannel = (ch) => {
    setForm(f => ({
      ...f,
      channels: f.channels.includes(ch) ? f.channels.filter(c => c !== ch) : [...f.channels, ch]
    }));
  };

  return (
    <div data-testid="admin-promotions-tab" className="space-y-6">
      <h2 className="font-serif text-2xl text-brand-text">Send Offers to Clients</h2>

      {/* Quick Templates */}
      <div className="border border-brand-border bg-brand-surface p-5">
        <p className="font-sans text-xs text-brand-gold font-semibold mb-3 uppercase tracking-wider">Quick Templates</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {templates.map((t) => (
            <button
              key={t.title}
              onClick={() => setForm(f => ({ ...f, title: t.title, message: t.message }))}
              data-testid={`promo-template-${t.title.replace(/\s/g, "-").toLowerCase()}`}
              className="text-left p-3 border border-brand-border hover:border-brand-gold/50 transition-colors"
            >
              <p className="font-sans text-sm text-brand-gold font-semibold">{t.title}</p>
              <p className="font-sans text-xs text-brand-text-muted mt-1 line-clamp-2">{t.message}</p>
            </button>
          ))}
        </div>
      </div>

      {/* Compose Message */}
      <div className="border border-brand-border bg-brand-surface p-5 space-y-4">
        <p className="font-sans text-xs text-brand-gold font-semibold uppercase tracking-wider">Compose Message</p>
        <Input
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder="Offer title (e.g. Weekend Special)"
          data-testid="promo-title-input"
          className="bg-brand-bg border-brand-border text-brand-text rounded-none"
        />
        <textarea
          value={form.message}
          onChange={(e) => setForm({ ...form, message: e.target.value })}
          placeholder="Your message... Use {name} to personalize. Include your phone (313) 484-7307 and website."
          rows={4}
          data-testid="promo-message-input"
          className="w-full bg-brand-bg border border-brand-border text-brand-text rounded-none px-3 py-2 font-sans text-sm placeholder:text-brand-text-muted/50 focus:outline-none focus:border-brand-gold"
        />
        <p className="font-sans text-[10px] text-brand-text-muted">{form.message.length}/160 characters (SMS optimal: under 160)</p>

        {/* Channels */}
        <div>
          <p className="font-sans text-xs text-brand-text-muted mb-2">Send via:</p>
          <div className="flex gap-2">
            {[
              { id: "sms", label: "SMS Text" },
              { id: "whatsapp", label: "WhatsApp" },
              { id: "email", label: "Email" },
            ].map((ch) => (
              <button
                key={ch.id}
                onClick={() => toggleChannel(ch.id)}
                data-testid={`promo-channel-${ch.id}`}
                className={`px-4 py-2 font-sans text-xs font-semibold border transition-colors ${
                  form.channels.includes(ch.id) ? "bg-brand-gold text-black border-brand-gold" : "bg-transparent text-brand-text-muted border-brand-border hover:border-brand-gold"
                }`}
              >
                {ch.label}
              </button>
            ))}
          </div>
        </div>

        {/* Recipients */}
        <div>
          <p className="font-sans text-xs text-brand-text-muted mb-2">Send to:</p>
          <div className="flex gap-2">
            <button
              onClick={() => setForm(f => ({ ...f, recipient_filter: "all" }))}
              data-testid="promo-recipients-all"
              className={`px-4 py-2 font-sans text-xs font-semibold border transition-colors ${
                form.recipient_filter === "all" ? "bg-brand-gold text-black border-brand-gold" : "bg-transparent text-brand-text-muted border-brand-border"
              }`}
            >
              All Clients ({contacts.length})
            </button>
            <button
              onClick={() => setForm(f => ({ ...f, recipient_filter: "custom" }))}
              data-testid="promo-recipients-custom"
              className={`px-4 py-2 font-sans text-xs font-semibold border transition-colors ${
                form.recipient_filter === "custom" ? "bg-brand-gold text-black border-brand-gold" : "bg-transparent text-brand-text-muted border-brand-border"
              }`}
            >
              Custom Numbers
            </button>
          </div>
          {form.recipient_filter === "custom" && (
            <Input
              value={form.custom_numbers}
              onChange={(e) => setForm({ ...form, custom_numbers: e.target.value })}
              placeholder="Phone numbers separated by commas (e.g. 3135551234, 2485556789)"
              data-testid="promo-custom-numbers"
              className="mt-2 bg-brand-bg border-brand-border text-brand-text rounded-none"
            />
          )}
        </div>

        {/* Send Button */}
        <button
          onClick={handleSend}
          disabled={sending}
          data-testid="promo-send-btn"
          className="flex items-center gap-2 px-8 py-3 bg-brand-gold text-black font-sans text-sm font-semibold hover:bg-brand-gold-light transition-colors disabled:opacity-50"
        >
          <Send size={16} /> {sending ? "Sending..." : "Send Offer Now"}
        </button>
      </div>

      {/* Contacts */}
      <div className="border border-brand-border bg-brand-surface p-5">
        <div className="flex items-center justify-between mb-4">
          <p className="font-sans text-xs text-brand-gold font-semibold uppercase tracking-wider">Client Contacts ({contacts.length})</p>
          <button
            onClick={() => setShowAddContact(!showAddContact)}
            data-testid="promo-add-contact-btn"
            className="flex items-center gap-1 px-3 py-1.5 bg-brand-gold text-black font-sans text-xs font-semibold hover:bg-brand-gold-light transition-colors"
          >
            <Plus size={12} /> Add Contact
          </button>
        </div>
        {showAddContact && (
          <div className="flex flex-col sm:flex-row gap-2 mb-4">
            <Input value={newContact.name} onChange={(e) => setNewContact({ ...newContact, name: e.target.value })} placeholder="Name" className="bg-brand-bg border-brand-border text-brand-text rounded-none flex-1" />
            <Input value={newContact.phone} onChange={(e) => setNewContact({ ...newContact, phone: e.target.value })} placeholder="Phone (3135551234)" className="bg-brand-bg border-brand-border text-brand-text rounded-none flex-1" data-testid="promo-new-phone" />
            <Input value={newContact.email} onChange={(e) => setNewContact({ ...newContact, email: e.target.value })} placeholder="Email (optional)" className="bg-brand-bg border-brand-border text-brand-text rounded-none flex-1" />
            <button onClick={handleAddContact} className="px-4 py-2 bg-brand-gold text-black font-sans text-xs font-semibold">Save</button>
          </div>
        )}
        <div className="max-h-48 overflow-y-auto space-y-1">
          {contacts.slice(0, 20).map((c, i) => (
            <div key={c.phone || c.email || i} className="flex items-center gap-4 py-1.5 border-b border-brand-border/50 last:border-0">
              <span className="font-sans text-xs text-brand-text flex-1">{c.name || "—"}</span>
              <span className="font-sans text-xs text-brand-text-muted">{c.phone}</span>
              <span className="font-sans text-xs text-brand-text-muted">{c.email || "—"}</span>
              <span className="font-sans text-[10px] text-brand-gold capitalize">{c.source}</span>
            </div>
          ))}
          {contacts.length > 20 && <p className="font-sans text-xs text-brand-text-muted text-center pt-2">...and {contacts.length - 20} more</p>}
          {contacts.length === 0 && <p className="font-sans text-xs text-brand-text-muted text-center py-4">No contacts yet. Contacts are auto-collected from bookings and walk-ins.</p>}
        </div>
      </div>

      {/* Campaign History */}
      {history.length > 0 && (
        <div className="border border-brand-border bg-brand-surface p-5">
          <p className="font-sans text-xs text-brand-gold font-semibold mb-3 uppercase tracking-wider">Campaign History</p>
          <div className="space-y-2">
            {history.map((h, i) => (
              <div key={h.id || h.created_at || i} className="flex items-center justify-between py-2 border-b border-brand-border/50 last:border-0">
                <div>
                  <p className="font-sans text-sm text-brand-text">{h.title}</p>
                  <p className="font-sans text-[10px] text-brand-text-muted">{new Date(h.created_at).toLocaleDateString()}</p>
                </div>
                <div className="flex gap-3 text-xs font-sans">
                  {h.results?.sms_sent > 0 && <span className="text-green-400">SMS: {h.results.sms_sent}</span>}
                  {h.results?.whatsapp_sent > 0 && <span className="text-green-400">WA: {h.results.whatsapp_sent}</span>}
                  {h.results?.email_sent > 0 && <span className="text-green-400">Email: {h.results.email_sent}</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default PromotionsTab;
